import React from "react";
import CodeBlock from "./CodeBlock";

/**
 * Markdown renderers for assistant answers.
 *
 * Deliberately not using @tailwindcss/typography's `prose` here. The old code
 * applied `prose prose-sm` to the streaming card but not to the finished
 * message, so an answer visibly restyled the instant it stopped streaming.
 * Styling every element explicitly means one set of rules covers both states
 * and there is no specificity fight between `prose` and these classes.
 */

/** Flattens a React child tree back to plain text, for the copy button. */
const nodeToText = (node) => {
  if (node === null || node === undefined || node === false) return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(nodeToText).join("");
  if (typeof node === "object" && node.props) return nodeToText(node.props.children);
  return "";
};

const ExternalLink = ({ href, children, ...props }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="text-iq-brand underline decoration-iq-brand/30 underline-offset-2 transition-colors hover:decoration-iq-brand"
    {...props}
  >
    {children}
  </a>
);

const markdownRenderers = {
  p: ({ node, ...props }) => <p className="mb-4 last:mb-0" {...props} />,

  // Children are passed explicitly rather than via the spread so
  // jsx-a11y/heading-has-content can see that these headings are not empty.
  h1: ({ node, children, ...props }) => (
    <h1
      className="mt-6 mb-3 text-[21px] font-semibold tracking-[-0.01em] first:mt-0"
      {...props}
    >
      {children}
    </h1>
  ),
  h2: ({ node, children, ...props }) => (
    <h2
      className="mt-6 mb-2.5 text-[18px] font-semibold tracking-[-0.01em] first:mt-0"
      {...props}
    >
      {children}
    </h2>
  ),
  h3: ({ node, children, ...props }) => (
    <h3 className="mt-5 mb-2 text-[16px] font-semibold first:mt-0" {...props}>
      {children}
    </h3>
  ),
  h4: ({ node, children, ...props }) => (
    <h4 className="mt-4 mb-1.5 text-[15px] font-semibold first:mt-0" {...props}>
      {children}
    </h4>
  ),

  ul: ({ node, ...props }) => (
    <ul
      className="mb-4 list-disc space-y-1.5 pl-5 last:mb-0 [&_ol]:mt-1.5 [&_ul]:mt-1.5"
      {...props}
    />
  ),
  ol: ({ node, ...props }) => (
    <ol
      className="mb-4 list-decimal space-y-1.5 pl-5 last:mb-0 [&_ol]:mt-1.5 [&_ul]:mt-1.5"
      {...props}
    />
  ),
  li: ({ node, ...props }) => (
    <li className="marker:text-iq-ink-faint" {...props} />
  ),

  a: ({ node, href, children, ...props }) => (
    <ExternalLink href={href} {...props}>
      {children}
    </ExternalLink>
  ),

  strong: ({ node, ...props }) => (
    <strong className="font-semibold text-iq-ink" {...props} />
  ),
  em: ({ node, ...props }) => <em className="italic" {...props} />,

  blockquote: ({ node, ...props }) => (
    <blockquote
      className="mb-4 border-l-2 border-iq-brand/30 pl-4 text-iq-ink-soft last:mb-0"
      {...props}
    />
  ),

  hr: () => <hr className="my-6 border-iq-line" />,

  // Wrapped so a wide table scrolls inside the message instead of stretching
  // the whole column.
  table: ({ node, children, ...props }) => (
    <div className="mb-4 overflow-x-auto rounded-lg border border-iq-line last:mb-0">
      <table className="w-full border-collapse text-[13.5px]" {...props}>
        {children}
      </table>
    </div>
  ),
  thead: ({ node, ...props }) => <thead className="bg-iq-rail" {...props} />,
  th: ({ node, ...props }) => (
    <th
      className="border-b border-iq-line px-3 py-2 text-left font-semibold text-iq-ink"
      {...props}
    />
  ),
  td: ({ node, ...props }) => (
    <td
      className="border-b border-iq-line px-3 py-2 align-top [tbody_tr:last-child_&]:border-b-0"
      {...props}
    />
  ),

  img: ({ node, alt, ...props }) => (
    <img
      alt={alt || ""}
      className="my-4 max-w-full rounded-lg border border-iq-line"
      {...props}
    />
  ),

  // Fenced code. `pre` renders CodeBlock and never renders its children, so
  // the `code` renderer below only ever sees inline code.
  pre: ({ node, children }) => {
    const child = Array.isArray(children) ? children[0] : children;
    const childClassName = child?.props?.className || "";
    const match = /language-([\w+#-]+)/.exec(childClassName);
    const code = nodeToText(child?.props?.children).replace(/\n$/, "");
    return <CodeBlock code={code} language={match ? match[1] : null} />;
  },

  code: ({ node, className, children, ...props }) => (
    <code
      className="iq-mono rounded-[5px] border border-iq-line bg-iq-rail px-1.5 py-[1px] text-[0.86em] text-iq-ink"
      {...props}
    >
      {children}
    </code>
  ),
};

export default markdownRenderers;
